import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, EMPTY, map, mergeMap, Observable, of, tap } from 'rxjs';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { AddFeedbackRequest, SessionGate } from '../../../../../backend/src/core/session/session.gate';
import { LambdaService } from '../../shared/core/lambda.service';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { Objection, ObjectionType } from '../objection/objection';
import { OpenaiModel } from '../openai/openai';
import { OpenaiService } from '../openai/openai.service';
import { PostHogEvent } from '../posthog/posthog.event';
import {
  evaluateFactsBasedObjectionAnswerFunctions,
  evaluateFactsBasedObjectionAnswerPrompt,
  evaluateSoftSkillsObjectionAnswerFunctions,
  evaluateSoftSkillsObjectionAnswerPrompt,
} from '../prompt/objection/evaluate-objection-answer.prompt';
import { getObjectionFunctions, getObjectionPrompt } from '../prompt/objection/get-objection.prompt';
import { SessionTemplate } from '../session-template/session-template';
import { SessionTemplateService } from '../session-template/session.template.service';
import { Session } from '../session/session';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { SessionMessage, SessionMessageBackend, SessionMessageSubData } from './session-message';

@Injectable({
  providedIn: 'root',
})
export class SessionMessageService {
  private _changeSubject = new BehaviorSubject<SessionMessage | undefined>(undefined);

  constructor(
    private sessionTemplateService: SessionTemplateService,
    private coreItemService: CoreItemService,
    private workspaceHolderService: WorkspaceHolderService,
    private lambdaService: LambdaService,
    private openaiService: OpenaiService
  ) {}

  get changed$() {
    return this._changeSubject.asObservable();
  }

  updateAiFeedback$(sessionMessage: SessionMessage) {
    posthog.capture(PostHogEvent.sessionUpdateAiFeedback);
    return this.coreItemService
      .updateCoreItem$(sessionMessage)
      .pipe(tap((_) => this._changeSubject.next(sessionMessage)));
  }

  addFeedback$(sessionMessageId: string, feedback: string) {
    const request: AddFeedbackRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      sessionMessageId,
      feedback,
    };

    return this.lambdaService.run$(ResourceName.session, SessionGate.actions.addFeedback, request).pipe(
      map((response: SessionMessageBackend) => {
        const result = this.coreItemService.updateCoreItemInCache(response) as SessionMessage;

        this._changeSubject.next(result);

        return result;
      })
    );
  }

  // TODO: update session message and leave the history.
  updateSessionMessageWithHistory$(sessionMessage: SessionMessage) {
    // return this.coreItemService
    //   .updateCoreItem$(sessionMessage)
    //   .pipe(tap((_) => this._changeSubject.next(sessionMessage)));
  }

  updateSessionMessage$(sessionMessage: SessionMessage): Observable<SessionMessage> {
    return this.coreItemService.updateCoreItem$(sessionMessage).pipe(
      map((data) => {
        this._changeSubject.next(sessionMessage);
        return data as SessionMessage;
      })
    );
  }

  getSessionMessageListByStartEndTime$(startTime: DateTime, endTime: DateTime) {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService
          .getCoreItemList$({
            coreTypeList: [CoreItemType.sessionMessage],
            dateRange: { startTime, endTime },
          })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as SessionMessage[];
            })
          );
      })
    );
  }

  getSessionMessageListById$(sessionId: string) {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({
          coreTypeList: [CoreItemType.sessionMessage],
          parentIdList: [sessionId],
        }) as Observable<SessionMessage[]>;
      })
    );
  }

  addSessionMessage$(session: Session, subData: SessionMessageSubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            coreType: CoreItemType.sessionMessage,
            subData,
            parentId: session.id,
          },
        ],
        notChangeLoadingStatus: true,
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as SessionMessage);
          return arr[0] as SessionMessage;
        })
      );
  }

  deleteSessionMessageList$(sessionMessageList: SessionMessage[]) {
    const deleteMetadataList = sessionMessageList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }

  findObjection$(sessionTemplate: SessionTemplate, sessionMessage: SessionMessage) {
    return this.sessionTemplateService.getEmotionalObjectionList$(sessionTemplate).pipe(
      mergeMap((objectionList) => {
        return this.openaiService
          .getAnswer$({
            messageList: [
              {
                role: 'user',
                content: getObjectionPrompt(objectionList, sessionMessage.subData.customerMessage),
              },
            ],
            config: {
              openaiModel: OpenaiModel.gpt4,
              functions: getObjectionFunctions(objectionList, sessionMessage.subData.customerMessage),
            },
          })
          .pipe(
            mergeMap((response: any) => {
              if (!response?.objectionId) {
                return EMPTY;
              }
              sessionMessage.subData.objectionId = response?.objectionId;
              return this.updateSessionMessage$(sessionMessage);
            })
          );
      })
    );
  }

  evaluateSoftSkillsObjectionAnswer$(curMessage: SessionMessage, messageList: SessionMessage[], objection: Objection) {
    if (objection.type !== ObjectionType.emotional) {
      return EMPTY;
    }
    return this.openaiService
      .getAnswer$({
        messageList: [
          {
            role: 'user',
            content: evaluateSoftSkillsObjectionAnswerPrompt(messageList, objection),
          },
        ],
        config: { openaiModel: OpenaiModel.gpt4, functions: evaluateSoftSkillsObjectionAnswerFunctions(objection) },
      })
      .pipe(
        mergeMap((response: any) => {
          if (response) {
            curMessage.subData.objectionScore = response?.score;
            curMessage.subData.objectionSuggestions = response?.suggestions;
            curMessage.subData.objectionImprovedResponse = response?.improvedResponse;

            console.log('-----evaluateSoftSkillsObjectionAnswer subData:', curMessage.subData);

            return this.updateSessionMessage$(curMessage);
          } else {
            return of(curMessage);
          }
        })
      );
  }

  evaluateFactBasedObjectionAnswer$(curMessage: SessionMessage, messageList: SessionMessage[], objection: Objection) {
    if (objection.type !== ObjectionType.fact) {
      return EMPTY;
    }

    return this.openaiService
      .getAnswer$({
        messageList: [
          {
            role: 'user',
            content: evaluateFactsBasedObjectionAnswerPrompt(messageList, objection),
          },
        ],
        config: {
          openaiModel: OpenaiModel.o1Mini /*functions: evaluateFactsBasedObjectionAnswerFunctions(objection)*/,
        },
      })
      .pipe(
        mergeMap((response) => {
          return this.openaiService.getAnswer$({
            messageList: [
              {
                role: 'user',
                content: `parse and convert the following text to json format: \`\`\`\n${response}\`\`\`\n`,
              },
            ],
            config: {
              openaiModel: OpenaiModel.gpt35,
              functions: evaluateFactsBasedObjectionAnswerFunctions(objection),
            },
          });
        }),
        mergeMap((response: any) => {
          if (response) {
            curMessage.subData.objectionScore = response?.score;
            curMessage.subData.objectionMissingInformationList = response?.objectionMissingInformationList;
            curMessage.subData.objectionInaccurateInformationList = response?.objectionInaccurateInformationList;

            console.log('-----evaluateFactBasedObjectionAnswer subData:', curMessage.subData);

            return this.updateSessionMessage$(curMessage);
          } else {
            return of(curMessage);
          }
        })
      );
  }

  updateFactBasedObjection$(objection: Objection, sessionMessageList: SessionMessage[]) {
    // return this.openaiService
    //   .getAnswer$({
    //     messageList: [
    //       {
    //         role: 'user',
    //         content: evaluateFactsBasedObjectionAnswerPrompt(sessionMessageList, objection),
    //       },
    //     ],
    //     config: {
    //       openaiModel: OpenaiModel.o1Mini /*functions: evaluateFactsBasedObjectionAnswerFunctions(objection)*/,
    //     },
    //   })
    //   .pipe(take(1))
    //   .subscribe((response) => {
    //     this.openaiService
    //       .getAnswer$({
    //         messageList: [
    //           {
    //             role: 'user',
    //             content: `parse and convert the following text to json format: \`\`\`\n${response}\`\`\`\n`,
    //           },
    //         ],
    //         config: {
    //           openaiModel: OpenaiModel.gpt35,
    //           functions: evaluateFactsBasedObjectionAnswerFunctions(objection),
    //         },
    //       })
    //       .pipe(take(1))
    //       .subscribe((response) => {
    //         if (response) {
    //           myAnswer.subData.objectionScore = response?.score;
    //           myAnswer.subData.objectionMissingInformationList = response?.objectionMissingInformationList;
    //           myAnswer.subData.objectionInaccurateInformationList = response?.objectionInaccurateInformationList;
    //           this.sessionMessageService.updateSessionMessage$(myAnswer).pipe(take(1)).subscribe();
    //         }
    //       });
    //   });
  }
}
